import { CommonModule, Location } from '@angular/common';
import { Component } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { ToastrService } from 'ngx-toastr';

import { DialogService } from 'primeng/dynamicdialog';
import { MenubarModule } from 'primeng/menubar';

import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';

import { MenuItem } from 'primeng/api';

import { ThemeService } from './theme.service';

import {
    AccessPermissions,
    BaseComponent,
    GlobalsService,
    LoginAsUserDTO,
    MetaService,
    ReadMetaRequestDTO,
    UnknownErrorCallingAPIMessage,
    firstOrDefault,
    isAssigned,
    isEmpty,
    reloadPage,
} from './data';
import { showLoginAsUser } from './shared/login-as-user/login-as-user.component';
import { UIBlockerComponent } from './data/components/ui-blocker/ui-blocker.componet';

const TabReportsUrl = '/reports';
const TabUserDirectoryUrl = '/user-directory';
const TabMyWorkUrl = '/';
const TabUserAccountUrl = '/account-information';

const ComponentName = 'with-menu';
@Component({
    standalone: true,
    imports: [CommonModule, RouterModule, MenubarModule, UIBlockerComponent],
    selector: `app-${ComponentName}`,
    templateUrl: './app-with-menu.component.html',
    providers: [DialogService],
    host: {
        class: 'flex flex-column overflow-hidden h-full',
    },
})
export class AppWithMenuComponent extends BaseComponent {
    startItems!: MenuItem[];
    endItems!: MenuItem[];
    userConnected!: AccountInfo;
    currentUser?: LoginAsUserDTO;
    isDarkMode: boolean;
    currentUserFT = false;
    languageManuÉnabled = false;

    constructor(
        private readonly _dialogSrv: DialogService,
        private readonly _authService: MsalService,
        private readonly _themeSrv: ThemeService,
        private readonly _router: Router,
        toastr: ToastrService,
        globals: GlobalsService,
        metaSrv: MetaService,
        location: Location,
        translateSrv: TranslateService,
    ) {
        super(toastr, globals, metaSrv, location, translateSrv);
        this._translateKey = `eaps.${ComponentName}`;

        this.isDarkMode = _themeSrv.isDarkMode;

        globals.currentUserChange$.subscribe((user) => {
            if (this.currentUserFT) {
                this.doReadMeta();
            } else {
                this.currentUserFT = true;
            }
        });
    }

    protected checkCurrentUrl(url: string): void {
        const path = (url?.split('?')[0].split('#')[0] ?? '').toLowerCase();

        this.languageManuÉnabled =
            path === TabMyWorkUrl || path === TabUserDirectoryUrl || path === TabReportsUrl || (path.startsWith(TabUserAccountUrl) && path.endsWith('/info'));

        this.loadMenuItems();
    }

    protected override onInit(): void {
        super.onInit();

        this.checkCurrentUrl(this._router.url);

        this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
            this.checkCurrentUrl(event.urlAfterRedirects);
        });

        this._themeSrv.switchTheme(this.isDarkMode);

        this.setLoginDisplay();
    }

    protected override fillReadMetaRequest(request: ReadMetaRequestDTO): void {
        if (isAssigned(this.userConnected?.idTokenClaims)) {
            request.userEmail = `${this.userConnected.idTokenClaims!['email']}`;
        }
    }

    protected override doReadMeta(): void {
        this.metaSrv.readPermisions().subscribe({
            next: (response) => {
                if (this.permissions.workTab) {
                    this._router.navigate([TabMyWorkUrl]);
                } else if (this.permissions.directoryTab) {
                    this._router.navigate([TabUserDirectoryUrl]);
                } else {
                    const userId = this.globals.currentUser?.userId;
                    if (isAssigned(userId)) {
                        this._router.navigate(['/account-information', userId, 'info']);
                    }
                }

                this.loadMenuItems();
            },
            error: (error) => {
                this.toastrError(error.message ?? UnknownErrorCallingAPIMessage);
            },
        });
    }

    private loadMenuItems() {
        this.startItems = [];

        if (this.permissions.workTab) {
            this.startItems.push({
                label: this.translate('tabMyWork.label'),
                icon: 'pi pi-fw pi-home',
                routerLink: [TabMyWorkUrl],
                routerLinkActiveOptions: { exact: true },
            });
        }

        if (this.permissions.directoryTab) {
            this.startItems.push({
                label: this.translate('tabUserDirectory.label'),
                icon: 'pi pi-fw pi-user',
                routerLink: [TabUserDirectoryUrl],
            });
        }

        if (this.permissions.reportsTab) {
            this.startItems.push({
                label: this.translate('tabReports.label'),
                icon: 'pi pi-fw pi-file',
                routerLink: [TabReportsUrl],
            });
        }

        this.endItems = [
            this.accountItems,
            this.languagesItems,
            { icon: 'pi pi-fw pi-sun', command: () => this.toggleDarkMode(), visible: this.isDarkMode },
            { icon: 'pi pi-fw pi-moon', command: () => this.toggleDarkMode(), visible: !this.isDarkMode },
        ];
    }

    setLoginDisplay() {
        if (this._authService.instance.getAllAccounts().length > 0) {
            this.userConnected = this._authService.instance.getAllAccounts()[0];

            if (isAssigned(this.userConnected?.idTokenClaims)) {
                const userEmail = `${this.userConnected.idTokenClaims!['email']}`;

                this.globals.loggedUser = {
                    userEmail: userEmail!,
                    userName: this.userConnected.name!,
                };
                this.globals.currentUser = this.globals.loggedUser;
            }
        }
    }

    private setLanguage(lang: string) {
        this.globals.language = lang;
        this.loadMenuItems();
    }

    get permissions(): AccessPermissions {
        return this.globals.permissions;
    }

    get languagesItems(): MenuItem {
        const lang = this.globals.language;
        const res: MenuItem = {
            label: lang.toUpperCase(),
            disabled: !this.languageManuÉnabled,
        };

        if (this.languageManuÉnabled) {
            res.items = [
                {
                    label: 'English',
                    disabled: lang === 'en',
                    command: () => this.setLanguage('en'),
                },
                {
                    label: 'Español',
                    disabled: lang === 'es',
                    command: () => this.setLanguage('es'),
                },
                {
                    label: 'Português',
                    disabled: lang === 'pt',
                    command: () => this.setLanguage('pt'),
                },
                // {
                //     label: 'Français',
                //     disabled: lang === 'fr',
                //     command: () => this.setLanguage('fr'),
                // },
            ];
        }

        return res;
    }

    get accountItems(): MenuItem {
        const currentUser = this.globals.currentUser;
        const loggedUser = this.globals.loggedUser;
        const diffUser = loggedUser?.userEmail.toLowerCase() !== currentUser?.userEmail.toLowerCase();
        let userName = currentUser?.userName;

        if (diffUser) {
            userName = this.translate('accountMenu.label', {
                loggedUser: loggedUser,
                currentUser: currentUser,
            });
        }

        const actItems: MenuItem[] = [
            {
                label: this.translate('myAccount.label'),
                command: () => this._router.navigate(['/account-information', currentUser?.userId, 'info']),
            },
        ];

        if (this.permissions.loginAs || diffUser) {
            actItems.push({
                label: this.translate('loginAs.label'),
                command: () => this.onLoginAsUser(),
            });

            if (diffUser) {
                actItems.push({
                    label: this.translate('returnUser.label', { userName: loggedUser?.userName }),
                    command: () => {
                        this.globals.currentUser = undefined;
                        const currentUser = this.globals.currentUser;

                        this.toastr.info(this.translate('loginAs.notification', currentUser));

                        reloadPage(this._router);
                    },
                });
            }
        }

        actItems.push({
            label: this.translate('logout.label'),
            icon: 'pi pi-fw pi-sign-out',
            command: () => this.onLogout(),
        });

        return {
            label: userName,
            icon: 'pi pi-fw pi-user-edit',
            items: actItems,
        };
    }

    onLogout() {
        this.globals.currentUser = undefined;
        this.globals.loggedUser = undefined;
        this._authService.logoutRedirect();
    }

    toggleDarkMode() {
        this.isDarkMode = !this.isDarkMode;

        this._themeSrv.switchTheme(this.isDarkMode);
        this.loadMenuItems();
    }

    onLoginAsUser() {
        const userLookup = 'loginAsUserLookup';

        showLoginAsUser(this._dialogSrv, (user) => {
            this.readMeta(
                (options) => {
                    const uu = firstOrDefault(options[userLookup]);

                    if (isAssigned(uu) && !isEmpty(uu?.val)) {
                        const userD = uu?.label?.split('|') ?? ['', '', ''];

                        this.globals.currentUser = {
                            userName: userD[0].trim(),
                            userEmail: userD[1].trim(),
                            userId: userD.length >= 3 ? Number(userD[2]?.trim()) : undefined,
                            userToken: uu?.val,
                        };

                        this.toastr.info(this.translate('loginAs.notification', user));

                        reloadPage(this._router);
                    }
                },
                [userLookup],
                (request) => {
                    request.filterText = user.userEmail;
                },
            );
        });
    }
}
